import { Link } from 'react-router-dom';
import { CheckCircle, FileText, Clock, Phone, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const NewPatients = () => {
  const whatToExpect = [
    {
      icon: Clock,
      title: 'Arrive Early',
      description: 'Please arrive 15 minutes before your scheduled appointment to complete any necessary paperwork.',
    },
    {
      icon: FileText,
      title: 'Private Consultation',
      description: 'Your appointment will begin with a thorough, confidential discussion of your concerns and medical history.',
    },
    {
      icon: CheckCircle,
      title: 'Evaluation',
      description: 'Depending on your concerns, this may include a physical examination and/or lab work.',
    },
    {
      icon: CreditCard,
      title: 'Clear Plan',
      description: "You'll leave with a clear understanding of your diagnosis and treatment options.",
    },
  ];

  const whatToBring = [
    'Valid photo ID (driver\'s license or state ID)',
    'Insurance card (if applicable)',
    'List of current medications (including dosages)',
    'Any relevant medical records or test results',
    'List of questions or concerns you\'d like to discuss',
  ];

  return (
    <div className="pb-20 md:pb-0" data-testid="new-patients-page">
      <SEO
        title="New Patients"
        description="New patient information for Havkin Urology. Learn what to expect, what to bring, and insurance details for your first urology visit in Melbourne, FL."
        keywords="new patient urology, first urology visit, what to bring urologist, urology insurance Melbourne FL"
        canonical="/new-patients"
      />
      {/* Hero */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-6">
              New Patients
            </h1>
            <p className="text-lg text-slate-600">
              We understand that visiting a urologist—especially for sensitive concerns—can feel daunting. Here's what you can expect when you visit our office.
            </p>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
            What to Expect
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whatToExpect.map((item, index) => (
              <div 
                key={index}
                className="text-center"
                data-testid={`expect-item-${index}`}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-brand/10 rounded-full mb-4">
                  <item.icon className="h-7 w-7 text-brand" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-primary mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Bring */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
                What to Bring
              </h2>
              <p className="text-slate-600 mb-8">
                To make your visit as efficient as possible, please bring the following items to your appointment:
              </p>
              <ul className="space-y-4">
                {whatToBring.map((item, index) => (
                  <li key={index} className="flex items-start" data-testid={`bring-item-${index}`}>
                    <CheckCircle className="h-5 w-5 text-brand flex-shrink-0 mr-3 mt-0.5" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-xl border border-slate-200">
              <h3 className="font-heading text-xl font-semibold text-primary mb-4">
                Medical Records
              </h3>
              <p className="text-slate-600 mb-6">
                If you have previous medical records, test results, or imaging related to your condition, please bring copies or have them sent to our office before your appointment.
              </p>
              <p className="text-slate-600 mb-6">
                To request records transfer or for any questions about preparing for your visit, please call our office.
              </p>
              <a
                href="tel:+13215004545"
                className="inline-flex items-center text-brand font-medium hover:text-brand-hover"
                data-testid="records-phone"
              >
                <Phone className="h-5 w-5 mr-2" />
                (321) 500-4545
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Insurance & Payment
            </h2>
            <p className="text-lg text-slate-600">
              We work with most major insurance providers. Please contact our office to verify your coverage before your appointment.
            </p>
          </div>

          {/* Dr. Havkin Mustache Photo */}
          <div className="max-w-sm mx-auto mb-10 rounded-xl overflow-hidden shadow-lg" data-testid="mustache-photo">
            <img
              src="/images/mustache-photo.jpg"
              alt="Dr. Boris Havkin - Havkin Urology"
              className="w-full h-auto"
            />
          </div>

          <div className="bg-slate-50 p-8 rounded-xl">
            <h3 className="font-heading text-xl font-semibold text-primary mb-6 text-center">Insurances Accepted</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              {['Blue Cross Blue Shield', 'UMR', 'GEHA', 'United Healthcare', 'Anthem'].map((ins) => (
                <div key={ins} className="bg-white rounded-lg p-4 text-center border border-slate-200" data-testid={`insurance-${ins.toLowerCase().replace(/\s+/g, '-')}`}>
                  <p className="font-medium text-primary text-sm">{ins}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-600 mb-6 text-center">
              For insurance verification or questions about payment options, please call:
            </p>
            <a
              href="tel:+13215004545"
              className="inline-flex items-center justify-center px-8 py-3 bg-brand text-white rounded-full font-medium hover:bg-brand-hover transition-colors"
              data-testid="insurance-phone"
            >
              <Phone className="h-5 w-5 mr-2" />
              (321) 500-4545
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-brand">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Schedule Your Visit?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Request an appointment online or give us a call to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-white text-brand hover:bg-slate-100 rounded-full px-8"
              data-testid="new-patients-cta-appointment"
            >
              <Link to="/contact">Request Appointment</Link>
            </Button>
            <a
              href="tel:+13215004545"
              className="inline-flex items-center justify-center px-8 py-3 bg-transparent border-2 border-white text-white rounded-full font-medium hover:bg-white/10 transition-colors"
              data-testid="new-patients-cta-call"
            >
              <Phone className="h-5 w-5 mr-2" />
              Call Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewPatients;
